/**
 * Design System Getproof - Card Component
 * Composant carte basé sur l'analyse du code existant
 */

import { forwardRef } from 'react';
import { cn } from '@/lib/utils';
import {
    CardProps,
    CardHeaderProps,
    CardContentProps,
    CardFooterProps
} from './Card.types';
import {
    cardStyles,
    cardHeaderStyles,
    cardContentStyles,
    cardFooterStyles
} from './Card.styles';

// Composant Card principal
export const Card = forwardRef<HTMLDivElement, CardProps>(
    (
        {
            variant = 'default',
            size,
            padding = 'md',
            hoverable = false,
            children,
            className,
            ...props
        },
        ref
    ) => {
        return (
            <div
                ref={ref}
                className={cn(
                    cardStyles.base,
                    cardStyles.variants[variant],
                    cardStyles.padding[padding],
                    size && cardStyles.sizes[size],
                    hoverable && cardStyles.hover[variant],
                    className
                )}
                {...props}
            >
                {children}
            </div>
        );
    }
);

Card.displayName = 'Card';

// Composant CardHeader
export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
    (
        {
            title,
            subtitle,
            actions,
            children,
            className,
            ...props
        },
        ref
    ) => {
        const hasActions = Boolean(actions);

        return (
            <div
                ref={ref}
                className={cn(
                    hasActions ? cardHeaderStyles.withActions : cardHeaderStyles.base,
                    className
                )}
                {...props}
            >
                {children ? (
                    children
                ) : (
                    <>
                        <div className="flex-1">
                            {title && (
                                <h3 className={cardHeaderStyles.title}>
                                    {title}
                                </h3>
                            )}
                            {subtitle && (
                                <p className={cardHeaderStyles.subtitle}>
                                    {subtitle}
                                </p>
                            )}
                        </div>
                        {actions && (
                            <div className="flex-shrink-0">
                                {actions}
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    }
);

CardHeader.displayName = 'CardHeader';

// Composant CardContent
export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
    (
        {
            children,
            className,
            ...props
        },
        ref
    ) => {
        return (
            <div
                ref={ref}
                className={cn(
                    cardContentStyles.base,
                    className
                )}
                {...props}
            >
                {children}
            </div>
        );
    }
);

CardContent.displayName = 'CardContent';

// Composant CardFooter
export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
    (
        {
            children,
            className,
            ...props
        },
        ref
    ) => {
        return (
            <div
                ref={ref}
                className={cn(
                    cardFooterStyles.base,
                    className
                )}
                {...props}
            >
                {children}
            </div>
        );
    }
);

CardFooter.displayName = 'CardFooter';